'use client';

import * as SheetPrimitive from '@radix-ui/react-dialog';
import { cva, type VariantProps } from 'class-variance-authority';
import { X } from 'lucide-react';
import * as React from 'react';

import { cn } from '../../lib/cn';

/** Radice del pannello laterale (sheet): gestisce apertura/chiusura. */
const Sheet = SheetPrimitive.Root;

/** Elemento che apre lo sheet al click. */
const SheetTrigger = SheetPrimitive.Trigger;

/** Elemento che chiude lo sheet al click. */
const SheetClose = SheetPrimitive.Close;

/** Portale che renderizza lo sheet fuori dal flusso DOM del genitore. */
const SheetPortal = SheetPrimitive.Portal;

/** Props di {@link SheetOverlay}: nessuna prop custom. */
export interface SheetOverlayProps extends React.ComponentPropsWithoutRef<
	typeof SheetPrimitive.Overlay
> {}

/** Sfondo scurito dietro lo sheet. */
const SheetOverlay = React.forwardRef<
	React.ElementRef<typeof SheetPrimitive.Overlay>,
	SheetOverlayProps
>(({ className, ...props }, ref) => (
	<SheetPrimitive.Overlay
		className={cn(
			'fixed inset-0 z-50 bg-black/80  data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0',
			className,
		)}
		{...props}
		ref={ref}
	/>
));
SheetOverlay.displayName = SheetPrimitive.Overlay.displayName;

const sheetVariants = cva(
	'fixed z-50 gap-4 bg-background p-4 shadow-lg transition ease-in-out data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:duration-300 data-[state=open]:duration-500',
	{
		variants: {
			side: {
				top: 'inset-x-0 top-0 border-b data-[state=closed]:slide-out-to-top data-[state=open]:slide-in-from-top',
				bottom:
					'inset-x-0 bottom-0 border-t data-[state=closed]:slide-out-to-bottom data-[state=open]:slide-in-from-bottom',
				left: 'inset-y-0 left-0 h-full w-3/4 border-r data-[state=closed]:slide-out-to-left data-[state=open]:slide-in-from-left sm:max-w-sm',
				right:
					'inset-y-0 right-0 h-full w-3/4  border-l data-[state=closed]:slide-out-to-right data-[state=open]:slide-in-from-right sm:max-w-sm',
			},
		},
		defaultVariants: {
			side: 'right',
		},
	},
);

export interface SheetContentProps extends React.ComponentPropsWithoutRef<
	typeof SheetPrimitive.Content
> {
	/** Lato dello schermo da cui esce il pannello. Default: `right`. */
	side?: VariantProps<typeof sheetVariants>['side'];
}

/** Pannello che scorre da un lato dello schermo (drawer), con overlay e bottone di chiusura. */
const SheetContent = React.forwardRef<
	React.ElementRef<typeof SheetPrimitive.Content>,
	SheetContentProps
>(({ side = 'right', className, children, ...props }, ref) => (
	<SheetPortal>
		<SheetOverlay />
		<SheetPrimitive.Content ref={ref} className={cn(sheetVariants({ side }), className)} {...props}>
			{children}
			<SheetPrimitive.Close className="absolute right-4 top-4 rounded-sm opacity-70 ring-offset-background transition-opacity hover:opacity-100 focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2 disabled:pointer-events-none data-[state=open]:bg-secondary">
				<X className="h-4 w-4" />
				<span className="sr-only">Close</span>
			</SheetPrimitive.Close>
		</SheetPrimitive.Content>
	</SheetPortal>
));
SheetContent.displayName = SheetPrimitive.Content.displayName;

/** Props di {@link SheetHeader}: nessuna prop custom, solo gli attributi HTML standard di un div. */
export interface SheetHeaderProps extends React.HTMLAttributes<HTMLDivElement> {}

/** Intestazione dello sheet: contiene tipicamente {@link SheetTitle} e {@link SheetDescription}. */
const SheetHeader = ({ className, ...props }: SheetHeaderProps) => (
	<div className={cn('flex flex-col space-y-2 text-center sm:text-left', className)} {...props} />
);
SheetHeader.displayName = 'SheetHeader';

/** Props di {@link SheetFooter}: nessuna prop custom, solo gli attributi HTML standard di un div. */
export interface SheetFooterProps extends React.HTMLAttributes<HTMLDivElement> {}

/** Piè dello sheet, tipicamente con i bottoni di azione. */
const SheetFooter = ({ className, ...props }: SheetFooterProps) => (
	<div
		className={cn('flex flex-col-reverse sm:flex-row sm:justify-end sm:space-x-2', className)}
		{...props}
	/>
);
SheetFooter.displayName = 'SheetFooter';

/** Props di {@link SheetTitle}: nessuna prop custom. */
export interface SheetTitleProps extends React.ComponentPropsWithoutRef<
	typeof SheetPrimitive.Title
> {}

/** Titolo dello sheet, obbligatorio per l'accessibilità. */
const SheetTitle = React.forwardRef<React.ElementRef<typeof SheetPrimitive.Title>, SheetTitleProps>(
	({ className, ...props }, ref) => (
		<SheetPrimitive.Title
			ref={ref}
			className={cn('text-lg font-semibold text-foreground', className)}
			{...props}
		/>
	),
);
SheetTitle.displayName = SheetPrimitive.Title.displayName;

/** Props di {@link SheetDescription}: nessuna prop custom. */
export interface SheetDescriptionProps extends React.ComponentPropsWithoutRef<
	typeof SheetPrimitive.Description
> {}

/** Testo descrittivo dello sheet, associato al titolo per l'accessibilità. */
const SheetDescription = React.forwardRef<
	React.ElementRef<typeof SheetPrimitive.Description>,
	SheetDescriptionProps
>(({ className, ...props }, ref) => (
	<SheetPrimitive.Description
		ref={ref}
		className={cn('text-sm text-muted-foreground', className)}
		{...props}
	/>
));
SheetDescription.displayName = SheetPrimitive.Description.displayName;

export {
	Sheet,
	SheetPortal,
	SheetOverlay,
	SheetTrigger,
	SheetClose,
	SheetContent,
	SheetHeader,
	SheetFooter,
	SheetTitle,
	SheetDescription,
};
